import { useState, useRef, useEffect } from "react";
import {
  Flame,
  Timer,
  Target,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  ChevronDown,
  Check,
} from "lucide-react";
import { useStore } from "../store/useStore";
import type { SortFilter } from "../store/slices/tagSlice";

const SORT_OPTIONS: {
  value: SortFilter;
  label: string;
  icon: typeof Flame;
}[] = [
  { value: "priority", label: "Priority", icon: Flame },
  { value: "urgent", label: "Urgent", icon: Timer },
  { value: "important", label: "Important", icon: Target },
  { value: "newest", label: "Newest", icon: ArrowDownNarrowWide },
  { value: "oldest", label: "Oldest", icon: ArrowUpNarrowWide },
];

export default function TagFilterPills() {
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const tags = useStore((s) => s.tags);
  const sharedTags = useStore((s) => s.sharedTags);
  const activeTagFilter = useStore((s) => s.activeTagFilter);
  const setActiveTagFilter = useStore((s) => s.setActiveTagFilter);
  const activeSortFilter = useStore((s) => s.activeSortFilter);
  const setActiveSortFilter = useStore((s) => s.setActiveSortFilter);

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!sortOpen) return;
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [sortOpen]);

  const currentSort =
    SORT_OPTIONS.find((o) => o.value === activeSortFilter) ?? SORT_OPTIONS[0];
  const SortIcon = currentSort.icon;

  const hasTags = tags.length > 0 || sharedTags.length > 0;

  return (
    <div className="flex items-center gap-2">
      {/* Sort dropdown */}
      <div className="relative flex-shrink-0" ref={sortRef}>
        <button
          onClick={() => setSortOpen((v) => !v)}
          className={`flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
            sortOpen
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border/60 bg-surface text-muted-foreground hover:text-foreground hover:border-border"
          }`}
        >
          <SortIcon className="w-3.5 h-3.5" />
          <span>{currentSort.label}</span>
          <ChevronDown
            className={`w-3 h-3 opacity-60 transition-transform ${sortOpen ? "rotate-180" : ""}`}
          />
        </button>

        {sortOpen && (
          <div className="absolute top-full left-0 mt-1.5 z-50 min-w-[152px] rounded-lg border border-border bg-popover shadow-raised py-1 animate-in fade-in-0 zoom-in-95">
            {SORT_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = activeSortFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => {
                    setActiveSortFilter(opt.value);
                    setSortOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${
                    isActive
                      ? "text-primary font-medium bg-primary/5"
                      : "text-foreground/80 hover:bg-muted/60"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="flex-1 text-left">{opt.label}</span>
                  {isActive && (
                    <Check className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Separator */}
      {hasTags && (
        <span className="w-px h-5 bg-border/60 flex-shrink-0" />
      )}

      {/* Tag pills */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar min-w-0">
        <button
          onClick={() => setActiveTagFilter(null)}
          className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            activeTagFilter === null
              ? "bg-primary/15 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
          }`}
        >
          All
        </button>

        {tags.map((tag) => (
          <button
            key={tag.id}
            onClick={() => setActiveTagFilter(tag.id)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTagFilter === tag.id
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
            }`}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: tag.color }}
            />
            {tag.name}
          </button>
        ))}

        {tags.length > 0 && (
          <button
            onClick={() => setActiveTagFilter("untagged")}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTagFilter === "untagged"
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
            }`}
          >
            Untagged
          </button>
        )}

        {sharedTags.map((st) => (
          <button
            key={st.tag_id}
            onClick={() => setActiveTagFilter(st.tag_id)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTagFilter === st.tag_id
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
            }`}
          >
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: st.tag_color }}
            />
            {st.tag_name}
            <span className="text-[10px] opacity-50">
              via {st.owner_name.split(" ")[0]}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
